import { camelizeKeys } from "humps";
import { ProfileCustomValuesList } from "PFApp/components/custom_values_list";
import { PlaceholderCustomValuesList } from "PFApp/components/custom_values_list/placeholder_custom_values_list";
import { SearchHighlights } from "PFApp/components/search_highlight";
import { Card } from "PFComponents/card";
import { Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { RenderIfVisible } from "PFComponents/render_if_visible";
import Table from "PFComponents/table";
import { PaginationOptions } from "PFComponents/table/table.types";
import { MatchedProfile } from "PFCore/types";
import { useEffect, useState } from "react";

import { useProfilesPageContext } from "../../context";
import css from "./profiles_table.module.scss";
import { useGenerateProfilesTableColumns } from "./use_generate_profiles_table_columns";

export const ProfilesTable = () => {
  const { profiles, meta, isFetching, setCurrentPage, selectedIds, setSelectedIds, scrollableContainerRef } =
    useProfilesPageContext();

  const [allRowsExpanded, setAllRowsExpanded] = useState(false);
  useEffect(() => {
    setAllRowsExpanded(!!meta.filters?.fields?.term.value);
  }, [meta.filters?.fields?.term]);

  const generateColumns = useGenerateProfilesTableColumns();

  const paginationOptions: PaginationOptions | undefined = meta && {
    currentPage: meta.page,
    totalPages: meta.totalPages,
    onClick: setCurrentPage
  };

  const renderExpandedRow = (matchedProfile: MatchedProfile) => {
    const { profile, optionalFields } = matchedProfile;
    const { highlights } = optionalFields || {};

    return (
      <Stack>
        {highlights && <SearchHighlights highlights={camelizeKeys(highlights)} alwaysExpanded />}
        {profile.skills && highlights && <Divider color="paletteNeutral1" />}
        {profile.skills && (
          <div className={css.expandedTableCustomValues}>
            <RenderIfVisible
              placeholder={<PlaceholderCustomValuesList customValues={profile.skills} />}
              scrollableContainerRef={scrollableContainerRef}
            >
              <ProfileCustomValuesList
                profileId={profile.id}
                customFieldName="skills"
                customValues={profile.skills}
                limit={10}
              />
            </RenderIfVisible>
          </div>
        )}
      </Stack>
    );
  };

  return (
    <Card>
      <Table
        generateColumns={generateColumns}
        tableData={profiles}
        paginationOptions={paginationOptions}
        isLoading={isFetching}
        enableRowExpansion
        disableAutoResizing
        renderExpandedRow={renderExpandedRow}
        expandedRows={allRowsExpanded || {}}
        enableRowSelection
        enableMultiRowSelection
        onRowSelectionChange={setSelectedIds}
        rowSelection={selectedIds}
        getRowId={({ id }) => String(id)}
        rowSelectionConditions={{
          isRowExpandable: ({ original: { profile, optionalFields } }) =>
            !!optionalFields?.highlights || !!profile.skills
        }}
      />
    </Card>
  );
};
